import type { FC } from "react";

export interface HudNavItemProps {
  label: string;
  /** Right-aligned secondary text in dim mono. Optional. */
  descriptor?: string;
  isActive: boolean;
  /** Indented one level — used for sub-items under a group header. */
  isChild?: boolean;
  /** Pulsing rose dot to flag attention (e.g. pending invites). */
  showAlertDot?: boolean;
  onClick: () => void;
}

/** Sidebar-style nav item in the HUD-CRT dialect.
 *
 * Active = bordered-pill silhouette with literal `[ LABEL ]` brackets,
 * emerald accent, dark-mode scanline overlay. The active item IS the
 * section header for the visible page, so it uses literal-bracket
 * vocabulary per the HUD-CRT two-vocabulary grammar.
 */
export const HudNavItem: FC<HudNavItemProps> = ({
  label,
  descriptor,
  isActive,
  isChild = false,
  showAlertDot = false,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={isActive ? "page" : undefined}
    className={`group relative flex items-center justify-between border px-3 py-2 text-left transition-colors ${
      isChild ? "ml-5 w-[calc(100%-1.25rem)]" : "w-full"
    } ${
      isActive
        ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:border-emerald-400/30 dark:bg-emerald-500/[0.06] dark:text-emerald-300"
        : "border-transparent text-slate-500 hover:border-slate-300 hover:bg-slate-100/60 hover:text-slate-700 dark:text-slate-500 dark:hover:border-slate-700/60 dark:hover:bg-slate-800/40 dark:hover:text-slate-300"
    }`}
  >
    {isActive && (
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden opacity-[0.06] dark:block"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, rgba(255,255,255,1) 0 1px, transparent 1px 3px)",
        }}
      />
    )}
    <span className="relative flex min-w-0 items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-widest">
      <span
        aria-hidden
        className={
          isActive
            ? "text-emerald-500/80 dark:text-emerald-400/80"
            : "text-slate-300 dark:text-slate-700"
        }
      >
        [
      </span>
      <span className="truncate">{label}</span>
      <span
        aria-hidden
        className={
          isActive
            ? "text-emerald-500/80 dark:text-emerald-400/80"
            : "text-slate-300 dark:text-slate-700"
        }
      >
        ]
      </span>
      {showAlertDot && (
        <span aria-hidden className="h-1.5 w-1.5 animate-pulse bg-rose-500" />
      )}
    </span>
    {descriptor && (
      <span
        className={`relative ml-2 flex-shrink-0 font-mono text-[9px] font-bold uppercase tracking-widest ${
          isActive
            ? "text-emerald-600/70 dark:text-emerald-400/60"
            : "text-slate-400 dark:text-slate-600"
        }`}
      >
        {descriptor}
      </span>
    )}
  </button>
);
